import {
  doc, getDoc, setDoc, updateDoc, addDoc, deleteDoc,
  collection, query, where, getDocs, serverTimestamp, Timestamp
} from "firebase/firestore";
import { db } from "../firebase";

/* ── BOOST CREATOR ── */
export async function boostCreator(userId, creatorId) {
  if (!userId || !creatorId) return { success: false, error: "Missing params" };
  if (userId === creatorId) return { success: false, error: "You can't boost yourself" };

  try {
    const boostedUntil = Timestamp.fromDate(new Date(Date.now() + 86400000)); // 24h
    await updateDoc(doc(db, "users", creatorId), {
      boosted: true,
      boostedUntil,
      boostedBy: userId,
    });
    await addDoc(collection(db, "boosts"), {
      userId,
      creatorId,
      boostedUntil,
      createdAt: serverTimestamp(),
    });
    return { success: true };
  } catch (err) {
    console.error("boostCreator error:", err);
    return { success: false, error: err.message };
  }
}

export async function checkBoostStatus(creatorId) {
  try {
    const snap = await getDoc(doc(db, "users", creatorId));
    if (!snap.exists()) return false;
    const data = snap.data();
    if (!data.boosted || !data.boostedUntil) return false;
    return data.boostedUntil.toDate() > new Date();
  } catch { return false; }
}

/* ── DROP ALERT ── */
export async function setDropAlert(userId, creatorId) {
  if (!userId || !creatorId) return { success: false, error: "Missing params" };
  try {
    const id = `${userId}_${creatorId}`;
    await setDoc(doc(db, "dropAlerts", id), {
      userId,
      creatorId,
      createdAt: serverTimestamp(),
      active: true,
    });
    return { success: true };
  } catch (err) {
    console.error("setDropAlert error:", err);
    return { success: false, error: err.message };
  }
}

export async function checkDropAlert(userId, creatorId) {
  try {
    const snap = await getDoc(doc(db, "dropAlerts", `${userId}_${creatorId}`));
    return snap.exists() && snap.data().active === true;
  } catch { return false; }
}

export async function removeDropAlert(userId, creatorId) {
  try {
    await deleteDoc(doc(db, "dropAlerts", `${userId}_${creatorId}`));
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/* ── SUPER COMMENT ── */
const BLOCKED_WORDS = [
  "fuck","shit","cunt","nigger","nigga","faggot","retard","bitch","asshole",
  "bastard","whore","slut","piss","dick","cock","pussy","arse","twat","wanker"
];

export function filterComment(text) {
  if (!text || text.trim().length === 0) return { ok: false, reason: "Comment can't be empty" };
  if (text.trim().length > 280) return { ok: false, reason: "Comment must be under 280 characters" };
  const lower = text.toLowerCase();
  const found = BLOCKED_WORDS.find(w => lower.includes(w));
  if (found) return { ok: false, reason: "Your comment contains language that isn't allowed" };
  return { ok: true };
}

export async function postSuperComment(userId, momentId, text, userDisplayName, userAvatar) {
  const filter = filterComment(text);
  if (!filter.ok) return { success: false, error: filter.reason };

  try {
    // Unpin any existing super comment on this moment first
    const existing = await getDocs(
      query(collection(db, "comments"),
        where("momentId", "==", momentId),
        where("isSuper", "==", true),
        where("pinned", "==", true)
      )
    );
    for (const d of existing.docs) {
      await updateDoc(d.ref, { pinned: false });
    }

    const ref = await addDoc(collection(db, "comments"), {
      momentId,
      userId,
      userDisplayName,
      userAvatar,
      text: text.trim(),
      isSuper: true,
      pinned: true,
      createdAt: serverTimestamp(),
      pinnedAt: serverTimestamp(),
    });

    return { success: true, commentId: ref.id };
  } catch (err) {
    console.error("postSuperComment error:", err);
    return { success: false, error: err.message };
  }
}

export async function removeSuperComment(commentId, originalUserId) {
  try {
    const snap = await getDoc(doc(db, "comments", commentId));
    if (!snap.exists()) return { success: false, error: "Comment not found" };
    await updateDoc(doc(db, "comments", commentId), {
      pinned: false,
      isSuper: false,
      removedByCreator: true,
    });
    // Refund credits to original commenter
    if (originalUserId) {
      const perksRef = doc(db, "userPerks", originalUserId);
      const perksSnap = await getDoc(perksRef);
      if (perksSnap.exists()) {
        const current = perksSnap.data().credits || 0;
        await updateDoc(perksRef, { credits: current + 50 }); // refund amount
        await addDoc(collection(db, "creditTransactions"), {
          userId: originalUserId,
          amount: 50,
          type: "super_comment_refund",
          description: "Super comment removed by creator — credits refunded",
          createdAt: serverTimestamp(),
        });
      }
    }
    return { success: true };
  } catch (err) {
    console.error("removeSuperComment error:", err);
    return { success: false, error: err.message };
  }
}